import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * 4Col_1Img_left Component
 * Displays a cart item with image on left and product details (name, quantity, price) on right
 * Includes heading and button text
 */
const FourColOneImgLeft = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    heading = "",
    title = "",
    quantity = "",
    price = "",
    image = null,
    buttonText = "",
    buttonLink = "#",
    component_name = "4Col_1Img_left",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localHeading, setLocalHeading] = useState(heading);
  const [localTitle, setLocalTitle] = useState(title);
  const [localQuantity, setLocalQuantity] = useState(quantity);
  const [localPrice, setLocalPrice] = useState(price);
  const [localImage, setLocalImage] = useState(image);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);

  useEffect(() => {
    setLocalHeading(heading || "");
    setLocalTitle(title || "");
    setLocalQuantity(quantity || "");
    setLocalPrice(price || "");
    setLocalImage(image);
    setLocalButtonText(buttonText || "");
    setLocalButtonLink(buttonLink || "#");
  }, [id, heading, title, quantity, price, image, buttonText, buttonLink]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      heading: localHeading,
      title: localTitle,
      quantity: localQuantity,
      price: localPrice,
      image: localImage,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
    });
    setIsEditing(false);
  };

  const handleImageUpload = (img) => {
    setLocalImage(img);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component four-col-one-img-left-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Cart Item</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Heading (Optional)</label>
          <input
            className="form-control"
            value={localHeading}
            onChange={(e) => setLocalHeading(e.target.value)}
            placeholder="Enter heading (e.g., YOUR CART)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Product Image</label>
          <EmailImageUpload
            initialImage={localImage}
            onImageUpload={handleImageUpload}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Product Name</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Product Name"
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Quantity</label>
            <input
              className="form-control"
              value={localQuantity}
              onChange={(e) => setLocalQuantity(e.target.value)}
              placeholder="Quantity"
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Price</label>
            <input
              className="form-control"
              value={localPrice}
              onChange={(e) => setLocalPrice(e.target.value)}
              placeholder="Price"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Text (Optional)</label>
          <input
            className="form-control"
            value={localButtonText}
            onChange={(e) => setLocalButtonText(e.target.value)}
            placeholder="Button text (e.g., Return To Your Cart >>)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Link</label>
          <input
            className="form-control"
            value={localButtonLink}
            onChange={(e) => setLocalButtonLink(e.target.value)}
            placeholder="Button link URL"
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const imageUrl = getImageUrl(localImage);

  return (
    <>
      {!previewMode && (
        <div className="email-component four-col-one-img-left mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Main container table */}
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              border="0"
              style={{
                maxWidth: "640px",
                margin: "0 auto",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              {/* Heading */}
              {localHeading && (
                <tr>
                  <td
                    align="center"
                    style={{
                      padding: "24px 16px 16px 16px",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "600",
                        color: primaryColor || "#333333",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      {localHeading}
                    </div>
                  </td>
                </tr>
              )}

              {/* Product row */}
              <tr>
                <td
                  style={{
                    padding: "16px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  <table
                    width="100%"
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      {/* Image column */}
                      <td
                        width="120"
                        valign="top"
                        style={{
                          paddingRight: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          width="120"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            borderRadius: "8px",
                            overflow: "hidden",
                            backgroundColor: "#f5f5f5",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              align="center"
                              valign="middle"
                              style={{
                                width: "120px",
                                height: "120px",
                                padding: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={localTitle || "Product"}
                                  width="120"
                                  height="120"
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    display: "block",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              ) : (
                                <img
                                  src="/img-preview.svg"
                                  alt="Product placeholder"
                                  width="80"
                                  height="80"
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    display: "block",
                                    opacity: 0.5,
                                  }}
                                />
                              )}
                            </td>
                          </tr>
                        </table>
                      </td>

                      {/* Product details column */}
                      <td
                        valign="top"
                        style={{
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {/* Header row */}
                          <tr>
                            <td
                              style={{
                                paddingBottom: "8px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <tr>
                                  <td
                                    width="33%"
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    Product Name
                                  </td>
                                  <td
                                    width="33%"
                                    align="center"
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    Quantity
                                  </td>
                                  <td
                                    width="34%"
                                    align="right"
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    Price
                                  </td>
                                </tr>
                              </table>
                            </td>
                          </tr>

                          {/* Data row */}
                          <tr>
                            <td
                              style={{
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <tr>
                                  <td
                                    width="33%"
                                    style={{
                                      fontSize: "14px",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {localTitle || "Product Name"}
                                  </td>
                                  <td
                                    width="33%"
                                    align="center"
                                    style={{
                                      fontSize: "14px",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {localQuantity || "1"}
                                  </td>
                                  <td
                                    width="34%"
                                    align="right"
                                    style={{
                                      fontSize: "14px",
                                      color: primaryColor || "#333333",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {localPrice || "0"}
                                  </td>
                                </tr>
                              </table>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>

              {/* Button */}
              {localButtonText && (
                <tr>
                  <td
                    align="center"
                    style={{
                      padding: "24px 16px",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <table
                      cellPadding="0"
                      cellSpacing="0"
                      border="0"
                      style={{
                        margin: "0 auto",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      <tr>
                        <td
                          align="center"
                          style={{
                            borderRadius: "999px",
                            border: "2px solid #d4a574",
                            padding: "12px 32px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <a
                            href={localButtonLink || "#"}
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: primaryColor || "#333333",
                              textDecoration: "none",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            {localButtonText}
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              )}
            </table>
          </td>
        </tr>
      </table>
    </>
  );
};

export default FourColOneImgLeft;
